import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    getDocs,
    doc,
    updateDoc,
    orderBy,
    limit,
    startAfter,
    endBefore,
    limitToLast,
    where,
    getCountFromServer
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import {
    SearchIcon,
    UserIcon,
    AcademicCapIcon,
    BadgeCheckIcon,
    CogIcon,
    CheckIcon
} from '@heroicons/react/outline';

const UserPermissions = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [updating, setUpdating] = useState({});
    const [successMessage, setSuccessMessage] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [firstDoc, setFirstDoc] = useState(null);
    const [lastDoc, setLastDoc] = useState(null);
    const [pageCache, setPageCache] = useState(new Map());
    const [searching, setSearching] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [roleStats, setRoleStats] = useState({
        administrator: 0,
        judge: 0,
        mentor: 0,
        applicant: 0
    });
    const [statsLoading, setStatsLoading] = useState(true);

    useEffect(() => {
        fetchUsers();
        fetchRoleStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Separate effect for role filter and usersPerPage (immediate)
    useEffect(() => {
        setCurrentPage(1);
        setPageCache(new Map());
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleFilter, usersPerPage]);

    // Separate effect for search with debouncing
    useEffect(() => {
        if (!searchTerm.trim()) {
            // If search is cleared, fetch immediately
            setCurrentPage(1);
            setPageCache(new Map());
            fetchUsers();
            return;
        }

        setSearchLoading(true);
        const delayedSearch = setTimeout(() => {
            setCurrentPage(1);
            setPageCache(new Map());
            fetchUsers();
        }, 800); // 800ms debounce for search

        return () => {
            clearTimeout(delayedSearch);
            setSearchLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const fetchRoleStats = useCallback(async () => {
        try {
            setStatsLoading(true);
            const usersRef = collection(db, 'users');
            const usersQuery = query(usersRef, where('hackathonRegistration', '==', true));
            const querySnapshot = await getDocs(usersQuery);

            const stats = {
                administrator: 0,
                judge: 0,
                mentor: 0,
                applicant: 0
            };

            querySnapshot.forEach((doc) => {
                const userData = doc.data();
                const role = userData.role?.toLowerCase() || 'applicant';

                if (role === 'administrator' || role === 'admin') {
                    stats.administrator++;
                } else if (role === 'judge') {
                    stats.judge++;
                } else if (role === 'mentor') {
                    stats.mentor++;
                } else {
                    stats.applicant++;
                }
            });

            setRoleStats(stats);
        } catch (error) {
            console.error('Error fetching role stats:', error);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    const searchUsers = useCallback(async (searchQuery, role = 'all') => {
        try {
            const usersRef = collection(db, 'users');
            const searchLower = searchQuery.toLowerCase().trim();
            let searchResults = [];

            // Check if the search query looks like an email (contains @)
            const isEmailSearch = searchLower.includes('@');

            if (isEmailSearch) {
                // For email searches, do exact database-level search
                let emailQuery = query(usersRef, where('hackathonRegistration', '==', true), where('email', '==', searchQuery.trim()));

                // Apply role filter if specified
                if (role !== 'all') {
                    emailQuery = query(usersRef, where('hackathonRegistration', '==', true), where('email', '==', searchQuery.trim()), where('role', '==', role));
                }

                const emailSnapshot = await getDocs(emailQuery);
                emailSnapshot.forEach((doc) => {
                    searchResults.push({ id: doc.id, ...doc.data() });
                });

                // Also try case-insensitive email search by fetching more records
                if (searchResults.length === 0) {
                    let caseInsensitiveQuery = query(usersRef, where('hackathonRegistration', '==', true), orderBy('email', 'asc'));
                    if (role !== 'all') {
                        caseInsensitiveQuery = query(usersRef, where('hackathonRegistration', '==', true), where('role', '==', role), orderBy('email', 'asc'));
                    }

                    const caseInsensitiveSnapshot = await getDocs(query(caseInsensitiveQuery, limit(1000)));
                    caseInsensitiveSnapshot.forEach((doc) => {
                        const userData = { id: doc.id, ...doc.data() };
                        if (userData.email?.toLowerCase() === searchLower) {
                            searchResults.push(userData);
                        }
                    });
                }
            } else {
                // For non-email searches, do the existing client-side filtering
                let searchQueryRef = query(usersRef, where('hackathonRegistration', '==', true), orderBy('email', 'asc'));

                // Apply role filter if specified
                if (role !== 'all') {
                    searchQueryRef = query(usersRef, where('hackathonRegistration', '==', true), where('role', '==', role), orderBy('email', 'asc'));
                }

                // Fetch documents and filter client-side
                const searchSnapshot = await getDocs(query(searchQueryRef));

                searchSnapshot.forEach((doc) => {
                    const userData = { id: doc.id, ...doc.data() };
                    if (
                        userData.email?.toLowerCase().includes(searchLower) ||
                        userData.firstName?.toLowerCase().includes(searchLower) ||
                        userData.lastName?.toLowerCase().includes(searchLower) ||
                        userData.displayName?.toLowerCase().includes(searchLower)
                    ) {
                        searchResults.push(userData);
                    }
                });
            }

            return {
                users: searchResults,
                totalCount: searchResults.length
            };
        } catch (error) {
            console.error('Error searching users:', error);
            return { users: [], totalCount: 0 };
        }
    }, []);

    const getTotalCount = useCallback(async () => {
        try {
            const usersRef = collection(db, 'users');
            let countQuery = query(usersRef, where('hackathonRegistration', '==', true));

            // Apply filters for counting
            if (roleFilter !== 'all') {
                countQuery = query(usersRef, where('hackathonRegistration', '==', true), where('role', '==', roleFilter));
            }

            // For search terms, we need to get count from the actual filtered results
            if (searchTerm.trim()) {
                const searchResults = await searchUsers(searchTerm, roleFilter);
                return searchResults.totalCount;
            }

            const countSnapshot = await getCountFromServer(countQuery);
            return countSnapshot.data().count;
        } catch (error) {
            console.error('Error getting count:', error);
            return 0;
        }
    }, [roleFilter, searchTerm, searchUsers]);

    const fetchUsers = useCallback(async (page = currentPage, direction = 'next') => {
        try {
            setLoading(page === 1 && !searchTerm.trim());
            setSearching(page !== 1 && !searchTerm.trim());

            const cacheKey = `${page}-${searchTerm}-${roleFilter}-${usersPerPage}`;

            // Check cache first
            if (pageCache.has(cacheKey)) {
                const cachedData = pageCache.get(cacheKey);
                setUsers(cachedData.users);
                setFirstDoc(cachedData.firstDoc);
                setLastDoc(cachedData.lastDoc);
                setSearchLoading(false);
                return;
            }

            // Handle search differently
            if (searchTerm.trim()) {
                const searchResults = await searchUsers(searchTerm, roleFilter);
                const startIndex = (page - 1) * usersPerPage;
                const endIndex = startIndex + usersPerPage;
                const paginatedUsers = searchResults.users.slice(startIndex, endIndex);

                setUsers(paginatedUsers);
                setTotalUsers(searchResults.totalCount);
                setTotalPages(Math.ceil(searchResults.totalCount / usersPerPage));
                setFirstDoc(null);
                setLastDoc(null);

                // Cache search results
                setPageCache(prev => new Map(prev.set(cacheKey, {
                    users: paginatedUsers,
                    firstDoc: null,
                    lastDoc: null
                })));

                setSearchLoading(false);
                return;
            }

            // Regular pagination for non-search queries
            const usersRef = collection(db, 'users');
            let usersQuery = query(usersRef, where('hackathonRegistration', '==', true), orderBy('email', 'asc'));

            // Apply role filter
            if (roleFilter !== 'all') {
                usersQuery = query(usersRef, where('hackathonRegistration', '==', true), where('role', '==', roleFilter), orderBy('email', 'asc'));
            }

            // Handle pagination
            if (page > 1 && direction === 'next' && lastDoc) {
                usersQuery = query(usersQuery, startAfter(lastDoc), limit(usersPerPage));
            } else if (page > 1 && direction === 'prev' && firstDoc) {
                usersQuery = query(usersQuery, endBefore(firstDoc), limitToLast(usersPerPage));
            } else {
                usersQuery = query(usersQuery, limit(usersPerPage));
            }

            const querySnapshot = await getDocs(usersQuery);
            const usersData = [];

            querySnapshot.forEach((doc) => {
                usersData.push({ id: doc.id, ...doc.data() });
            });

            const newFirstDoc = querySnapshot.docs[0];
            const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

            setUsers(usersData);
            setFirstDoc(newFirstDoc);
            setLastDoc(newLastDoc);

            // Cache the results
            setPageCache(prev => new Map(prev.set(cacheKey, {
                users: usersData,
                firstDoc: newFirstDoc,
                lastDoc: newLastDoc
            })));

            // Get total count for pagination
            const total = await getTotalCount();
            setTotalUsers(total);
            setTotalPages(Math.ceil(total / usersPerPage));

        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
            setSearching(false);
            setSearchLoading(false);
        }
    }, [currentPage, searchTerm, roleFilter, usersPerPage, lastDoc, firstDoc, pageCache, getTotalCount, searchUsers]); const handlePageChange = (page) => {
        if (page === currentPage) return;

        const direction = page > currentPage ? 'next' : 'prev';
        setCurrentPage(page);
        fetchUsers(page, direction);
    };

    const handleUsersPerPageChange = (newUsersPerPage) => {
        setUsersPerPage(newUsersPerPage);
        setCurrentPage(1);
        setPageCache(new Map()); // Clear cache when changing page size
    };

    const updateUserRole = async (userId, newRole) => {
        try {
            setUpdating(prev => ({ ...prev, [userId]: true }));

            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                role: newRole
            });

            // Update local state
            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.id === userId ? { ...user, role: newRole } : user
                )
            );

            // Refresh role statistics
            fetchRoleStats();

            setSuccessMessage(`User role updated to ${newRole} successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (error) {
            console.error('Error updating user role:', error);
        } finally {
            setUpdating(prev => ({ ...prev, [userId]: false }));
        }
    };

    const getRoleIcon = (role) => {
        switch (role?.toLowerCase()) {
            case 'administrator':
            case 'admin':
                return <CogIcon className="h-5 w-5 text-purple-600" />;
            case 'judge':
                return <BadgeCheckIcon className="h-5 w-5 text-orange-600" />;
            case 'mentor':
                return <AcademicCapIcon className="h-5 w-5 text-blue-600" />;
            default:
                return <UserIcon className="h-5 w-5 text-gray-600" />;
        }
    };

    const getRoleBadge = (role) => {
        const normalizedRole = role?.toLowerCase() || 'applicant';

        switch (normalizedRole) {
            case 'administrator':
            case 'admin':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        <CogIcon className="w-3 h-3 mr-1" />
                        Administrator
                    </span>
                );
            case 'judge':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <BadgeCheckIcon className="w-3 h-3 mr-1" />
                        Judge
                    </span>
                );
            case 'mentor':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <AcademicCapIcon className="w-3 h-3 mr-1" />
                        Mentor
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <UserIcon className="w-3 h-3 mr-1" />
                        Applicant
                    </span>
                );
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">User Permissions</h1>
                <p className="text-gray-600">Manage user roles and permissions</p>
            </div>

            {/* Role Statistics Cards */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Administrator Card */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Administrators</p>
                            <p className="text-3xl font-bold text-purple-500 mt-2">
                                {statsLoading ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                                ) : (
                                    roleStats.administrator
                                )}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-50">
                            <CogIcon className="h-8 w-8 text-purple-500" />
                        </div>
                    </div>
                </div>

                {/* Judge Card */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Judges</p>
                            <p className="text-3xl font-bold text-orange-500 mt-2">
                                {statsLoading ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                ) : (
                                    roleStats.judge
                                )}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50">
                            <BadgeCheckIcon className="h-8 w-8 text-orange-500" />
                        </div>
                    </div>
                </div>

                {/* Mentor Card */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Mentors</p>
                            <p className="text-3xl font-bold text-blue-500 mt-2">
                                {statsLoading ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                ) : (
                                    roleStats.mentor
                                )}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50">
                            <AcademicCapIcon className="h-8 w-8 text-blue-500" />
                        </div>
                    </div>
                </div>

                {/* Applicant Card */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Applicants</p>
                            <p className="text-3xl font-bold text-gray-500 mt-2">
                                {statsLoading ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
                                ) : (
                                    roleStats.applicant
                                )}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-gray-50">
                            <UserIcon className="h-8 w-8 text-gray-500" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex">
                        <CheckIcon className="h-5 w-5 text-green-400" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">
                                {successMessage}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search by email or name..."
                        className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={loading}
                    />
                    {searchLoading && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1"></div>
                        </div>
                    )}
                </div>

                {/* Role Filter */}
                <div className="relative">
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        disabled={loading || searchLoading}
                    >
                        <option value="all">All Roles</option>
                        <option value="administrator">Administrator</option>
                        <option value="judge">Judge</option>
                        <option value="mentor">Mentor</option>
                        <option value="applicant">Applicant</option>
                    </select>
                </div>
            </div>

            {/* Results Summary and Items Per Page */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <p className="text-sm text-gray-600">
                        {searchLoading ? (
                            <span className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1 mr-2"></div>
                                Searching...
                            </span>
                        ) : searching ? (
                            'Loading more results...'
                        ) : (
                            <>
                                Showing {users.length > 0 ? ((currentPage - 1) * usersPerPage) + 1 : 0} to{' '}
                                {Math.min(currentPage * usersPerPage, Math.min(totalUsers, ((currentPage - 1) * usersPerPage) + users.length))} of {totalUsers} users
                                {(searchTerm.trim() || roleFilter !== 'all') && ` (filtered)`}
                            </>
                        )}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                        Show:
                    </label>
                    <select
                        id="itemsPerPage"
                        value={usersPerPage}
                        onChange={(e) => handleUsersPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                        disabled={searching || searchLoading}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-600">per page</span>
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white shadow-sm overflow-hidden rounded-lg border">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Current Role
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Joined Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Change Role
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {searchLoading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-blue-1 mr-2"></div>
                                            Searching users...
                                        </div>
                                    </td>
                                </tr>
                            ) : searching ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-blue-1 mr-2"></div>
                                            Loading more results...
                                        </div>
                                    </td>
                                </tr>
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                                        {searchTerm.trim() ? 'No users found matching your search' : 'No users found'}
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                                    {getRoleIcon(user.role)}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {user.firstName && user.lastName
                                                            ? `${user.firstName} ${user.lastName}`
                                                            : user.displayName || 'N/A'
                                                        }
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {user.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getRoleBadge(user.role)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(user.joinedAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex space-x-2">
                                                {/* Judge Button */}
                                                <button
                                                    onClick={() => updateUserRole(user.id, 'Judge')}
                                                    disabled={updating[user.id] || user.role === 'Judge'}
                                                    className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${user.role === 'Judge'
                                                        ? 'bg-orange-200 text-orange-800 cursor-not-allowed'
                                                        : 'text-orange-600 bg-orange-100 hover:bg-orange-200 focus:ring-orange-500'
                                                        }`}
                                                >
                                                    {updating[user.id] ? (
                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                                                    ) : (
                                                        <>
                                                            <BadgeCheckIcon className="w-3 h-3 mr-1" />
                                                            Judge
                                                        </>
                                                    )}
                                                </button>

                                                {/* Mentor Button */}
                                                <button
                                                    onClick={() => updateUserRole(user.id, 'Mentor')}
                                                    disabled={updating[user.id] || user.role === 'Mentor'}
                                                    className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${user.role === 'Mentor'
                                                        ? 'bg-blue-200 text-blue-800 cursor-not-allowed'
                                                        : 'text-blue-600 bg-blue-100 hover:bg-blue-200 focus:ring-blue-500'
                                                        }`}
                                                >
                                                    {updating[user.id] ? (
                                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                                    ) : (
                                                        <>
                                                            <AcademicCapIcon className="w-3 h-3 mr-1" />
                                                            Mentor
                                                        </>
                                                    )}
                                                </button>

                                                {/* Reset to Applicant */}
                                                {(user.role === 'Judge' || user.role === 'Mentor' || user.role === 'Administrator') && (
                                                    <button
                                                        onClick={() => updateUserRole(user.id, 'applicant')}
                                                        disabled={updating[user.id]}
                                                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                                    >
                                                        {updating[user.id] ? (
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                                                        ) : (
                                                            <>
                                                                <UserIcon className="w-3 h-3 mr-1" />
                                                                Reset
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                let pageNumber;
                                if (totalPages <= 7) {
                                    pageNumber = i + 1;
                                } else if (currentPage <= 4) {
                                    pageNumber = i + 1;
                                } else if (currentPage >= totalPages - 3) {
                                    pageNumber = totalPages - 6 + i;
                                } else {
                                    pageNumber = currentPage - 3 + i;
                                }

                                if (pageNumber < 1 || pageNumber > totalPages) return null;

                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${pageNumber === currentPage
                                            ? 'bg-custom-blue-1 text-white'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>

                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                </div>
            )}


        </div>
    );
};

export default UserPermissions;
